import { vec3 } from 'gl-matrix';

/**
 * Given an array of imageIds, sort them based on their imagePositionPatient, and
 * also returns the spacing between images and the origin of the reference image
 *
 * @param imageIds - array of imageIds
 * @param scanAxisNormal - [x, y, z] array or gl-matrix vec3
 *
 * @returns The sortedImageIds, zSpacing, and origin of the first image in the series.
 */
export default function sortInstances(instances: Array<any>) {
  // Return if only one instance e.g., multiframe
  if (instances.length <= 1) {
    return instances;
  }

  const { ImagePositionPatient: referenceImagePositionPatient, ImageOrientationPatient } =
    instances[Math.floor(instances.length / 2)]; // this prevents getting scout image as test image

  // Defensive: some datasets omit/mangle IPP/IOP on some instances. In that case,
  // avoid crashing during vec3 math and just return the original order.
  const isValidVec3 = (v: any) =>
    Array.isArray(v) && v.length >= 3 && v.slice(0, 3).every(Number.isFinite);
  const isValidIop = (v: any) =>
    Array.isArray(v) && v.length >= 6 && v.slice(0, 6).every(Number.isFinite);

  if (!isValidVec3(referenceImagePositionPatient) || !isValidIop(ImageOrientationPatient)) {
    return instances;
  }

  const rowCosineVec = vec3.fromValues(
    ImageOrientationPatient[0],
    ImageOrientationPatient[1],
    ImageOrientationPatient[2]
  );
  const colCosineVec = vec3.fromValues(
    ImageOrientationPatient[3],
    ImageOrientationPatient[4],
    ImageOrientationPatient[5]
  );

  const scanAxisNormal = vec3.cross(vec3.create(), rowCosineVec, colCosineVec);

  const refIppVec = vec3.set(
    vec3.create(),
    referenceImagePositionPatient[0],
    referenceImagePositionPatient[1],
    referenceImagePositionPatient[2]
  );
  // `refIppVec` is currently unused but keeping it is fine; it also validates ref IPP indices.

  const distanceInstancePairs = instances.map(instance => {
    const imagePositionPatient = instance.ImagePositionPatient;
    if (!isValidVec3(imagePositionPatient)) {
      // If any IPP is invalid, bail out of sorting to prevent vec3.sub throwing.
      return { distance: 0, instance, __invalid: true as const };
    }

    const positionVector = vec3.create();

    vec3.sub(positionVector, referenceImagePositionPatient, imagePositionPatient);

    const distance = vec3.dot(positionVector, scanAxisNormal);

    return {
      distance,
      instance,
    };
  });

  // If any instance had invalid IPP, don't sort at all (keeps viewer stable).
  if (distanceInstancePairs.some((p: any) => p.__invalid)) {
    return instances;
  }

  distanceInstancePairs.sort((a, b) => b.distance - a.distance);

  const sortedInstances = distanceInstancePairs.map(a => a.instance);

  return sortedInstances;
}
